import type { AddressState } from '@paid-ui/enums/address';
import type { UserConsentId, UserConsentType } from '@paid-ui/enums/consent';
import type {
  FeatureDeployStatus,
  FeatureGroup,
  FeatureItem,
  FeatureToggleStatus,
} from '@paid-ui/enums/feature';
import type { Browser, OperatingSystem } from 'detect-browser';

import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { browserEnvs } from '@paid-ui/env';
import { utc2MelStr } from '@paid-ui/utils/datetime';

const { title, description, maxFileSize, env } = browserEnvs;

export interface Announcement {
  type: 'MAINTENANCE';
  message: string;
  startDate: string;
  endDate: string;
}

export interface Feature {
  name: FeatureItem;
  displayName: string;
  description?: string;
  featureGroup: FeatureGroup;
  toggleStatus: FeatureToggleStatus;
  deployStatus: FeatureDeployStatus;
  active: boolean;
  userSubscribable: boolean;
  subscribed?: boolean;
}

export interface StateDepositInsuranceDisplay {
  displayName: string;
}

export interface ConsentItem {
  version: string;
  source: {
    id: UserConsentId;
    link: string;
  }[];
}

export interface RemoteConfig {
  announcements: Announcement[];
  configs: {
    EARLY_RELEASE_MIN_AMOUNT: number;
    EARLY_RELEASE_MIN_DISCOUNT_AMOUNT: number;
  };
  consentItems?: Record<UserConsentType, ConsentItem>;
  features: Record<FeatureItem, Feature>;
  fees: {
    EARLY_RELEASE_DISCOUNT: number;
  };
  gstRate: number;
  /**
   * Current savings rate as a fraction — 0.027 is 2.7%, the same scale as
   * `UserAccount.interestRate`. Null when no rate row is currently in effect.
   */
  savingsInterestRate: number | null;
  stateDepositInsurances?: Record<AddressState, StateDepositInsuranceDisplay>;
}

export const appConfigManager = proxy({
  title,
  description,
  maxFileSize,
  os: null as OperatingSystem | NodeJS.Platform | null,
  browser: null as Browser | 'bot' | 'node' | 'react-native' | null,
  env,
  mapId: '7050deb153077aa7',

  externalLinks: {
    runningProjectsOnPaidVideo: 'https://paid.inc/videos/running-projects-on-paid',
    afterShareContract: 'https://paid.inc/videos/after-share-contract',
    creatingContractsVideo: 'https://paid.inc/videos/creating-contract',
    reviewingContractsVideo: 'https://paid.inc/videos/reviewing-contract',
    signingContractsVideo: 'https://paid.inc/videos/signing-contract',
    abicPaidVideo: 'https://paid.inc/videos/abic-paid',
    mbaPaidVideo: 'https://paid.inc/videos/mba-paid',
    paidReVideo: 'https://paid.inc/videos/paid-re',
    funderVideo: 'https://paid.inc/videos/funder',
    trilogyFundsPaidVideo: 'https://paid.inc/videos/trilogyxpaid',
    secureConstructionInvestmentsVideo: 'https://paid.inc/videos/secure-construction-investments',
    escrowPaymentProtectionVideo: 'https://paid.inc/videos/escrow',
    smartContractManagementVideo: 'https://paid.inc/videos/smart-contract-management',
  },

  // Remote app config
  announcements: [] as Announcement[],
  earlyRelease: {
    discountFeeRate: 0.2,
    discountRatePrecision: 10,
    minAmount: 100,
    minDiscountAmount: 1,
  },
  gstRate: 10,
  savingsInterestRate: null as number | null,
  stateDepositInsurances: undefined as
    | Record<AddressState, StateDepositInsuranceDisplay>
    | undefined,
  consentItems: undefined as Record<UserConsentType, ConsentItem> | undefined,
});

devtools(appConfigManager, {
  name: 'App Config',
  enabled: false,
});

interface BrowserEnv {
  name: Browser | 'bot' | 'node' | 'react-native' | null;
  os: OperatingSystem | NodeJS.Platform | null;
}

export const saveBrowserEnv = (context: BrowserEnv) => {
  appConfigManager.browser = context.name;
  appConfigManager.os = context.os;
};

export const saveRemoteConfig = (remoteConfig: RemoteConfig) => {
  const {
    announcements,
    configs,
    consentItems,
    fees,
    gstRate,
    savingsInterestRate,
    stateDepositInsurances,
  } = remoteConfig;
  const { EARLY_RELEASE_MIN_AMOUNT, EARLY_RELEASE_MIN_DISCOUNT_AMOUNT } = configs;
  const { EARLY_RELEASE_DISCOUNT } = fees;

  appConfigManager.announcements = announcements;
  appConfigManager.gstRate = gstRate;
  // Null when no rate is in effect, and absent altogether against an API predating the
  // field — both must land as null rather than NaN, so the promo simply omits the rate.
  const parsedSavingsRate = Number(savingsInterestRate ?? Number.NaN);
  appConfigManager.savingsInterestRate = Number.isFinite(parsedSavingsRate)
    ? parsedSavingsRate
    : null;
  appConfigManager.stateDepositInsurances = stateDepositInsurances;
  appConfigManager.consentItems = consentItems;
  appConfigManager.earlyRelease.discountFeeRate = Number(EARLY_RELEASE_DISCOUNT);
  appConfigManager.earlyRelease.minAmount = Number(EARLY_RELEASE_MIN_AMOUNT);
  appConfigManager.earlyRelease.minDiscountAmount = Number(EARLY_RELEASE_MIN_DISCOUNT_AMOUNT);
};

export const getMaintenance = (announcements: Announcement[]) => {
  const maintenance = announcements.find(({ type }) => type === 'MAINTENANCE');
  return maintenance
    ? {
        type: 'MAINTENANCE',
        message: maintenance.message,
        startDate: utc2MelStr(maintenance.startDate, 'D MMM YYYY'),
        endDate: utc2MelStr(maintenance.endDate, 'D MMM YYYY'),
      }
    : null;
};
